/**
 * API Error Event Bridge
 *
 * Emits error events from the axios interceptor so the React Toast system
 * can subscribe and display them. This bridges non-React code with React context.
 */

type ErrorListener = (message: string) => void;

const listeners: Set<ErrorListener> = new Set();

export function onApiError(listener: ErrorListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitApiError(message: string): void {
  listeners.forEach((fn) => fn(message));
}

/**
 * Extract a user-friendly message from an Axios error
 */
export function extractErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'An unexpected error occurred';

  const err = error as Record<string, unknown>;

  // Axios error with response
  if (err.response && typeof err.response === 'object') {
    const resp = err.response as Record<string, unknown>;
    const data = resp.data as Record<string, unknown> | undefined;

    if (data) {
      if (typeof data.error === 'string') return data.error;
      if (typeof data.message === 'string') return data.message;
    }

    const status = resp.status as number;
    if (status === 401) return 'Session expired. Please log in again.';
    if (status === 403) return 'You don\'t have permission for this action.';
    if (status === 404) return 'The requested resource was not found.';
    if (status === 429) return 'Too many requests. Please wait a moment.';
    if (status >= 500) return 'Server error. Please try again later.';
  }

  // Network / timeout errors
  if (err.code === 'ERR_NETWORK') return 'Network unavailable. Check your connection.';
  if (err.code === 'ECONNABORTED') return 'Request timed out. Please try again.';

  if (typeof err.message === 'string') return err.message;

  return 'An unexpected error occurred';
}
